import React, { useEffect, useState } from 'react';
import { useNavigate, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { api, resolveImage } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { LogOut, Plus, Trash2, Edit3, Users, FileText, Upload, X, Save, Download, Mail, Phone, TrendingUp } from 'lucide-react';

const tokenKey = 'etech_admin_token';

const AdminLogin = () => {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      const { token } = await api.adminLogin(password);
      localStorage.setItem(tokenKey, token);
      nav('/admin/leads');
    } catch (e) {
      setErr('Password errata');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#2b2b2b] flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-[#1e1e1e] border border-[#e9dfc0]/15 rounded-2xl p-8">
        <div className="w-14 h-14 flex items-center justify-center mb-6 mx-auto">
          <img src="/etech-logo.png" alt="ETech" className="w-14 h-14 object-contain" />
        </div>
        <h1 className="font-serif text-white text-3xl text-center mb-2">Admin</h1>
        <p className="text-[#e9dfc0]/60 text-sm text-center mb-8">Accedi per gestire lead e casi studio</p>
        <form onSubmit={submit} className="space-y-4">
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/50 rounded-lg py-3.5 px-4 focus:outline-none focus:border-[#e9dfc0]" />
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <button type="submit" disabled={loading} className="w-full py-3.5 rounded-full bg-[#e9dfc0] text-[#2b2b2b] font-medium hover:bg-white transition-colors disabled:opacity-60">
            {loading ? 'Accesso in corso…' : 'Entra'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminShell = ({ children }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);

  const refreshUnread = () => api.unreadCount().then((r) => setUnread(r.count || 0)).catch(() => {});
  useEffect(() => { refreshUnread(); const t = setInterval(refreshUnread, 30000); return () => clearInterval(t); }, [loc.pathname]);

  const logout = async () => {
    try { await api.adminLogout(); } catch (err) { console.error('Logout API failed (proceeding with local cleanup):', err); }
    localStorage.removeItem(tokenKey);
    nav('/admin');
  };
  const tabs = [
    { to: '/admin/leads', icon: Users, label: 'Lead', badge: unread },
    { to: '/admin/case-studies', icon: FileText, label: 'Casi Studio' },
  ];
  return (
    <div className="min-h-screen bg-[#141414]">
      <header className="bg-[#1e1e1e] border-b border-[#e9dfc0]/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin/leads" className="flex items-center gap-3">
            <img src="/etech-logo.png" alt="ETech" className="w-10 h-10 object-contain" />
            <span className="text-white font-serif text-xl">Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            {tabs.map((t) => (
              <Link key={t.to} to={t.to} className={`relative px-4 py-2 rounded-full text-sm flex items-center gap-2 transition-colors ${loc.pathname.startsWith(t.to) ? 'bg-[#e9dfc0] text-[#2b2b2b]' : 'text-[#e9dfc0]/80 hover:text-white'}`}>
                <t.icon className="w-4 h-4" />{t.label}
                {t.badge > 0 && (
                  <span className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center">
                    {t.badge > 99 ? '99+' : t.badge}
                  </span>
                )}
              </Link>
            ))}
            <button onClick={logout} className="ml-2 px-4 py-2 rounded-full text-sm text-[#e9dfc0]/70 hover:text-white flex items-center gap-2">
              <LogOut className="w-4 h-4" />Esci
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10">{React.cloneElement(children, { refreshUnread })}</main>
    </div>
  );
};

const sourceLabel = { home: 'Home', tm: 'Temporary Management', contatti: 'Contatti' };
const sourceColor = { home: '#e9dfc0', tm: '#f0b876', contatti: '#a8c4a2' };

const AnalyticsPanel = ({ leads }) => {
  const total = leads.length;
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - 6);
  startOfWeek.setHours(0, 0, 0, 0);

  // Group by source
  const bySource = {};
  leads.forEach((l) => { bySource[l.source] = (bySource[l.source] || 0) + 1; });

  // Last 7 days
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(now.getDate() - i);
    d.setHours(0, 0, 0, 0);
    const next = new Date(d);
    next.setDate(d.getDate() + 1);
    const count = leads.filter((l) => {
      const t = new Date(l.created_at);
      return t >= d && t < next;
    }).length;
    days.push({ label: d.toLocaleDateString('it-IT', { weekday: 'short' }), date: d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }), count });
  }
  const maxCount = Math.max(1, ...days.map((d) => d.count));
  const weekTotal = days.reduce((s, d) => s + d.count, 0);
  const prevWeekCount = leads.filter((l) => {
    const t = new Date(l.created_at);
    const start = new Date(startOfWeek); start.setDate(start.getDate() - 7);
    return t >= start && t < startOfWeek;
  }).length;
  const delta = prevWeekCount === 0 ? (weekTotal > 0 ? 100 : 0) : Math.round(((weekTotal - prevWeekCount) / prevWeekCount) * 100);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
      {/* Total & delta */}
      <div className="bg-[#1e1e1e] border border-[#e9dfc0]/10 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[#e9dfc0]/60 text-xs uppercase tracking-wider">Ultimi 7 giorni</span>
          <TrendingUp className="w-4 h-4 text-[#e9dfc0]/60" />
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-serif text-white text-5xl">{weekTotal}</div>
          <div className={`text-sm font-medium ${delta >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {delta >= 0 ? '+' : ''}{delta}%
          </div>
        </div>
        <p className="text-[#e9dfc0]/60 text-xs mt-1">vs settimana precedente ({prevWeekCount})</p>
        <p className="text-[#e9dfc0]/50 text-xs mt-3">Totale lead: <span className="text-[#e9dfc0]">{total}</span></p>
      </div>

      {/* Per source */}
      <div className="bg-[#1e1e1e] border border-[#e9dfc0]/10 rounded-xl p-6">
        <span className="text-[#e9dfc0]/60 text-xs uppercase tracking-wider">Lead per fonte</span>
        <div className="mt-4 space-y-3">
          {Object.keys(sourceLabel).map((key) => {
            const count = bySource[key] || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#e9dfc0]/80">{sourceLabel[key]}</span>
                  <span className="text-[#e9dfc0]"><span className="font-medium">{count}</span> <span className="text-[#e9dfc0]/50">· {pct}%</span></span>
                </div>
                <div className="h-2 bg-[#2b2b2b] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: sourceColor[key] }} />
                </div>
              </div>
            );
          })}
          {total === 0 && <p className="text-[#e9dfc0]/40 text-xs">Nessun lead ancora</p>}
        </div>
      </div>

      {/* Weekly trend */}
      <div className="bg-[#1e1e1e] border border-[#e9dfc0]/10 rounded-xl p-6">
        <span className="text-[#e9dfc0]/60 text-xs uppercase tracking-wider">Trend settimanale</span>
        <div className="mt-4 flex items-end justify-between gap-2 h-24">
          {days.map((d, i) => {
            const h = d.count === 0 ? 4 : Math.max(6, (d.count / maxCount) * 88);
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group" title={`${d.date}: ${d.count} lead`}>
                <div className="text-[#e9dfc0]/60 text-[10px] h-3">{d.count > 0 ? d.count : ''}</div>
                <div className="w-full bg-[#e9dfc0]/80 rounded-t group-hover:bg-[#e9dfc0] transition-colors" style={{ height: `${h}px`, minHeight: '4px' }} />
                <div className="text-[#e9dfc0]/50 text-[10px] capitalize">{d.label.replace('.', '')}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const LeadsPage = ({ refreshUnread }) => {
  const [leads, setLeads] = useState([]);
  const [err, setErr] = useState('');
  const [unreadIds, setUnreadIds] = useState(new Set());

  useEffect(() => {
    api.listLeads().then((data) => {
      setLeads(data);
      setUnreadIds(new Set(data.filter((l) => !l.read).map((l) => l.id)));
      // mark all as read on visit
      api.markLeadsRead().then(() => refreshUnread && refreshUnread()).catch(() => {});
    }).catch(() => setErr('Errore di caricamento'));
  }, [refreshUnread]);

  const exportCsv = () => {
    window.location.href = api.exportLeadsUrl();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-white text-4xl">Lead ricevuti</h1>
          <p className="text-[#e9dfc0]/60 text-sm mt-1">{leads.length} totali {unreadIds.size > 0 && <span className="text-red-400">· {unreadIds.size} nuovi</span>}</p>
        </div>
        <button onClick={exportCsv} disabled={leads.length === 0} className="px-5 py-2.5 rounded-full bg-[#e9dfc0] text-[#2b2b2b] font-medium flex items-center gap-2 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          <Download className="w-4 h-4" />Esporta CSV (Excel)
        </button>
      </div>
      {err && <p className="text-red-400">{err}</p>}
      <AnalyticsPanel leads={leads} />
      <div className="bg-[#1e1e1e] border border-[#e9dfc0]/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-[#2b2b2b] text-[#e9dfc0]/70 text-left text-xs uppercase tracking-wider">
            <tr>
              <th className="p-4"></th><th className="p-4">Data</th><th className="p-4">Fonte</th><th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Telefono</th><th className="p-4">Interesse</th><th className="p-4">Messaggio</th><th className="p-4">Azioni</th>
            </tr>
          </thead>
          <tbody className="text-[#e9dfc0]/90">
            {leads.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-[#e9dfc0]/50">Nessun lead ancora. Compila un form dal sito per testare.</td></tr>
            )}
            {leads.map((l) => {
              const isNew = unreadIds.has(l.id);
              const subject = encodeURIComponent(`Re: la tua richiesta a ETech`);
              const body = encodeURIComponent(`Ciao ${l.name},\n\ngrazie per averci contattato tramite il sito ETech.\n\n`);
              const mailto = `mailto:${l.email}?subject=${subject}&body=${body}`;
              const tel = l.phone ? `tel:${l.phone.replace(/\s+/g, '')}` : null;
              return (
                <tr key={l.id} className={`border-t border-[#e9dfc0]/10 hover:bg-[#2b2b2b]/50 ${isNew ? 'bg-[#2b2b2b]/30' : ''}`}>
                  <td className="p-4">{isNew && <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="Nuovo" />}</td>
                  <td className="p-4 whitespace-nowrap">{new Date(l.created_at).toLocaleString('it-IT')}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full bg-[#e9dfc0]/15 text-[#e9dfc0] text-xs whitespace-nowrap">{sourceLabel[l.source] || l.source}</span></td>
                  <td className="p-4 whitespace-nowrap text-white">{l.name} {l.company && <span className="text-[#e9dfc0]/60">/ {l.company}</span>}</td>
                  <td className="p-4"><a href={`mailto:${l.email}`} className="text-[#e9dfc0] hover:underline">{l.email}</a></td>
                  <td className="p-4 whitespace-nowrap">{l.phone || '—'}</td>
                  <td className="p-4">{l.interest || '—'}</td>
                  <td className="p-4 max-w-xs truncate" title={l.message}>{l.message || '—'}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <a href={mailto} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#e9dfc0] text-[#2b2b2b] text-xs font-medium hover:bg-white transition-colors whitespace-nowrap">
                        <Mail className="w-3.5 h-3.5" />Rispondi
                      </a>
                      {tel ? (
                        <a href={tel} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#e9dfc0]/15 text-[#e9dfc0] text-xs font-medium hover:bg-[#e9dfc0]/25 transition-colors whitespace-nowrap">
                          <Phone className="w-3.5 h-3.5" />Chiama
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#e9dfc0]/5 text-[#e9dfc0]/40 text-xs whitespace-nowrap cursor-not-allowed" title="Nessun telefono">
                          <Phone className="w-3.5 h-3.5" />Chiama
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

const emptyCS = { slug: '', title: '', excerpt: '', image: '', gallery: [], metric: '', time: '', tag: 'Temporary Management Full', startPoint: '', actions: [''], results: [{ k: '', v: '' }], testimonial: '', published: true };

const slugify = (text) =>
  (text || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // remove non-alphanumerics
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);

const CSEditor = ({ initial, onCancel, onSaved }) => {
  const [form, setForm] = useState(() => ({ ...emptyCS, ...(initial || {}), gallery: (initial && initial.gallery) || [] }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const [uploading, setUploading] = useState(false);

  const extractError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (err?.message) return `${fallback}: ${err.message}`;
    return fallback;
  };

  const upload = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setUploading(true); setErr('');
    try {
      const f = await compressImage(raw);
      const { url } = await api.uploadImage(f);
      setField('image', url);
    } catch (err) {
      setErr(extractError(err, 'Upload cover fallito'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setErr(''); setUploading(true);
    try {
      const uploaded = [];
      for (const raw of files) {
        const f = await compressImage(raw);
        const { url } = await api.uploadImage(f);
        uploaded.push(url);
      }
      setField('gallery', [...(form.gallery || []), ...uploaded]);
    } catch (err) {
      setErr(extractError(err, 'Upload galleria fallito'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeGalleryImage = (idx) => {
    setField('gallery', form.gallery.filter((_, i) => i !== idx));
  };

  const moveGalleryImage = (idx, dir) => {
    const arr = [...form.gallery];
    const next = idx + dir;
    if (next < 0 || next >= arr.length) return;
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    setField('gallery', arr);
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      // Auto-generate slug from title if empty (only on create)
      let slug = (form.slug || '').trim();
      if (!initial && !slug) slug = slugify(form.title);
      // Normalize slug just in case
      slug = slugify(slug);
      if (!slug) {
        setErr('Aggiungi un titolo o uno slug valido prima di salvare.');
        setSaving(false);
        return;
      }
      if (!(form.title || '').trim()) {
        setErr('Il titolo è obbligatorio.');
        setSaving(false);
        return;
      }
      const payload = {
        ...form,
        slug,
        actions: form.actions.filter((a) => a.trim()),
        results: form.results.filter((r) => r.k && r.v),
      };
      if (initial) await api.updateCS(initial.slug, payload);
      else await api.createCS(payload);
      onSaved();
    } catch (e) {
      setErr(extractError(e, 'Errore salvataggio'));
    } finally { setSaving(false); }
  };

  // Auto-fill slug from title while typing (only for new case studies)
  const onTitleChange = (v) => {
    setForm((prev) => {
      const wasAutoSlug = !prev.slug || prev.slug === slugify(prev.title);
      return { ...prev, title: v, slug: initial ? prev.slug : (wasAutoSlug ? slugify(v) : prev.slug) };
    });
  };

  return (
    <div className="bg-[#1e1e1e] border border-[#e9dfc0]/10 rounded-xl p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-white text-3xl">{initial ? 'Modifica caso studio' : 'Nuovo caso studio'}</h2>
        <button onClick={onCancel} className="text-[#e9dfc0]/70 hover:text-white flex items-center gap-2"><X className="w-4 h-4" />Annulla</button>
      </div>
      {err && <p className="text-red-400 mb-4">{err}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Slug (URL)" value={form.slug} onChange={(v) => setField('slug', slugify(v))} disabled={!!initial} placeholder="Lascia vuoto: generato dal titolo" />
        <Input label="Tag" value={form.tag} onChange={(v) => setField('tag', v)} placeholder="Es. Advisory, Growth Partnership, Temporary Management Full" />
        <Input label="Titolo" value={form.title} onChange={onTitleChange} full />
        <Input label="Excerpt (2-3 righe)" value={form.excerpt} onChange={(v) => setField('excerpt', v)} full textarea />
        <Input label="Metrica principale" value={form.metric} onChange={(v) => setField('metric', v)} placeholder="+50k€/mese" />
        <Input label="Tempo" value={form.time} onChange={(v) => setField('time', v)} placeholder="4 mesi" />
      </div>

      <div className="mt-6">
        <label className="block text-[#e9dfc0]/80 text-sm mb-2">Immagine di copertina</label>
        <div className="flex items-center gap-4">
          {form.image && <img src={resolveImage(form.image)} alt="cover" className="w-32 h-20 rounded-lg object-cover border border-[#e9dfc0]/20" />}
          <label className={`px-5 py-2.5 rounded-full bg-[#e9dfc0] text-[#2b2b2b] text-sm font-medium flex items-center gap-2 hover:bg-white ${uploading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}>
            <Upload className="w-4 h-4" />{uploading ? 'Carico…' : 'Carica immagine'}
            <input type="file" accept="image/*" onChange={upload} disabled={uploading} className="hidden" />
          </label>
          <input value={form.image} onChange={(e) => setField('image', e.target.value)} placeholder="o incolla URL"
            className="flex-1 bg-[#2b2b2b] border border-[#4a4a4a] text-white rounded-lg py-2.5 px-4 text-sm focus:outline-none focus:border-[#e9dfc0]" />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-[#e9dfc0]/80 text-sm">Galleria immagini aggiuntive <span className="text-[#e9dfc0]/50 text-xs">(mostrate nel dettaglio del caso studio)</span></label>
          <label className={`px-4 py-2 rounded-full bg-[#e9dfc0]/15 text-[#e9dfc0] text-xs font-medium flex items-center gap-1.5 hover:bg-[#e9dfc0]/25 ${uploading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}>
            <Upload className="w-3.5 h-3.5" />{uploading ? 'Carico…' : 'Aggiungi immagini'}
            <input type="file" accept="image/*" multiple onChange={uploadGallery} disabled={uploading} className="hidden" />
          </label>
        </div>
        {form.gallery && form.gallery.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {form.gallery.map((url, idx) => (
              <div key={`${idx}-${url.slice(0, 20)}`} className="relative group aspect-video rounded-lg overflow-hidden border border-[#e9dfc0]/15">
                <img src={resolveImage(url)} alt={`gallery ${idx + 1}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button type="button" onClick={() => moveGalleryImage(idx, -1)} disabled={idx === 0} className="w-8 h-8 rounded-full bg-[#e9dfc0]/20 text-[#e9dfc0] hover:bg-[#e9dfc0]/40 disabled:opacity-30 flex items-center justify-center text-sm" title="Sposta a sinistra">←</button>
                  <button type="button" onClick={() => moveGalleryImage(idx, 1)} disabled={idx === form.gallery.length - 1} className="w-8 h-8 rounded-full bg-[#e9dfc0]/20 text-[#e9dfc0] hover:bg-[#e9dfc0]/40 disabled:opacity-30 flex items-center justify-center text-sm" title="Sposta a destra">→</button>
                  <button type="button" onClick={() => removeGalleryImage(idx)} className="w-8 h-8 rounded-full bg-red-500/80 text-white hover:bg-red-500 flex items-center justify-center" title="Rimuovi">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-semibold">{idx + 1}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#e9dfc0]/40 text-xs">Nessuna immagine in galleria. Clicca "Aggiungi immagini" per caricarle (selezione multipla supportata).</p>
        )}
      </div>

      <div className="mt-6">
        <Input label="Punto di partenza" value={form.startPoint} onChange={(v) => setField('startPoint', v)} full textarea />
      </div>

      <div className="mt-6">
        <label className="block text-[#e9dfc0]/80 text-sm mb-2">Azioni concrete</label>
        {form.actions.map((a, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={a} onChange={(e) => { const arr = [...form.actions]; arr[i] = e.target.value; setField('actions', arr); }}
              className="flex-1 bg-[#2b2b2b] border border-[#4a4a4a] text-white rounded-lg py-2.5 px-4 text-sm focus:outline-none focus:border-[#e9dfc0]" />
            <button onClick={() => setField('actions', form.actions.filter((_, j) => j !== i))} className="px-3 text-[#e9dfc0]/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => setField('actions', [...form.actions, ''])} className="text-[#e9dfc0] text-sm flex items-center gap-1 hover:text-white"><Plus className="w-4 h-4" />Aggiungi azione</button>
      </div>

      <div className="mt-6">
        <label className="block text-[#e9dfc0]/80 text-sm mb-2">Risultati (KPI)</label>
        {form.results.map((r, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={r.k} placeholder="Nome KPI (es. ROAS)" onChange={(e) => { const arr = [...form.results]; arr[i] = { ...arr[i], k: e.target.value }; setField('results', arr); }}
              className="flex-1 bg-[#2b2b2b] border border-[#4a4a4a] text-white rounded-lg py-2.5 px-4 text-sm focus:outline-none focus:border-[#e9dfc0]" />
            <input value={r.v} placeholder="Valore (es. 3.4x)" onChange={(e) => { const arr = [...form.results]; arr[i] = { ...arr[i], v: e.target.value }; setField('results', arr); }}
              className="flex-1 bg-[#2b2b2b] border border-[#4a4a4a] text-white rounded-lg py-2.5 px-4 text-sm focus:outline-none focus:border-[#e9dfc0]" />
            <button onClick={() => setField('results', form.results.filter((_, j) => j !== i))} className="px-3 text-[#e9dfc0]/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={() => setField('results', [...form.results, { k: '', v: '' }])} className="text-[#e9dfc0] text-sm flex items-center gap-1 hover:text-white"><Plus className="w-4 h-4" />Aggiungi KPI</button>
      </div>

      <div className="mt-6">
        <Input label="Testimonianza (opzionale)" value={form.testimonial} onChange={(v) => setField('testimonial', v)} full textarea />
      </div>

      <div className="mt-6 flex items-center gap-3">
        <label className="flex items-center gap-2 text-[#e9dfc0]/80 text-sm">
          <input type="checkbox" checked={form.published} onChange={(e) => setField('published', e.target.checked)} />
          Pubblicato (visibile sul sito)
        </label>
      </div>

      <div className="mt-8 flex gap-3">
        <button onClick={save} disabled={saving} className="px-6 py-3 rounded-full bg-[#e9dfc0] text-[#2b2b2b] font-medium hover:bg-white flex items-center gap-2 disabled:opacity-60">
          <Save className="w-4 h-4" />{saving ? 'Salvo…' : 'Salva'}
        </button>
        <button onClick={onCancel} className="px-6 py-3 rounded-full border border-[#e9dfc0]/30 text-[#e9dfc0]">Annulla</button>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, placeholder, full, textarea, disabled }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-[#e9dfc0]/80 text-sm mb-2">{label}</label>
    {textarea ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3}
        className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/40 rounded-lg py-2.5 px-4 text-sm focus:outline-none focus:border-[#e9dfc0] resize-none" />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
        className="w-full bg-[#2b2b2b] border border-[#4a4a4a] text-white placeholder:text-[#e9dfc0]/40 rounded-lg py-2.5 px-4 text-sm focus:outline-none focus:border-[#e9dfc0] disabled:opacity-60" />
    )}
  </div>
);

const CaseStudiesPage = () => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null = list; 'new' | {cs}
  const [err, setErr] = useState('');

  const load = () => api.adminListCS().then(setList).catch(() => setErr('Errore caricamento'));
  useEffect(() => { load(); }, []);

  const remove = async (cs) => {
    if (!window.confirm('Eliminare questo caso studio?')) return;
    try {
      if (cs.slug && cs.slug.trim()) {
        await api.deleteCS(cs.slug);
      } else {
        await api.deleteCSById(cs.id);
      }
    } catch (e) {
      alert('Errore eliminazione: ' + (e.response?.data?.detail || e.message));
    }
    load();
  };

  if (editing === 'new') return <CSEditor onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;
  if (editing && typeof editing === 'object') return <CSEditor initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-white text-4xl">Casi Studio</h1>
        <button onClick={() => setEditing('new')} className="px-5 py-2.5 rounded-full bg-[#e9dfc0] text-[#2b2b2b] font-medium flex items-center gap-2 hover:bg-white">
          <Plus className="w-4 h-4" />Nuovo
        </button>
      </div>
      {err && <p className="text-red-400 mb-4">{err}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((cs) => (
          <div key={cs.slug} className="bg-[#1e1e1e] border border-[#e9dfc0]/10 rounded-xl overflow-hidden">
            <div className="aspect-[16/9] bg-cover bg-center" style={{ backgroundImage: `url(${resolveImage(cs.image)})` }} />
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-serif text-[#e9dfc0] text-2xl">{cs.metric}</span>
                <span className="text-[#e9dfc0]/50 text-sm">· {cs.time}</span>
                {!cs.published && <span className="ml-auto px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 text-xs">Bozza</span>}
              </div>
              <h3 className="text-white font-serif text-lg mb-1 line-clamp-2">{cs.title}</h3>
              <p className="text-[#e9dfc0]/50 text-xs mb-4">/{cs.slug}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing(cs)} className="flex-1 px-3 py-2 rounded-lg bg-[#e9dfc0]/10 text-[#e9dfc0] text-sm flex items-center justify-center gap-1 hover:bg-[#e9dfc0]/20">
                  <Edit3 className="w-4 h-4" />Modifica
                </button>
                <button onClick={() => remove(cs)} className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {list.length === 0 && <p className="text-[#e9dfc0]/50 text-center py-16">Nessun caso studio. Crea il primo.</p>}
    </div>
  );
};

const Protected = ({ children }) => {
  const token = localStorage.getItem(tokenKey);
  if (!token) return <Navigate to="/admin" replace />;
  return <AdminShell>{children}</AdminShell>;
};

const Admin = () => (
  <Routes>
    <Route index element={<AdminLogin />} />
    <Route path="leads" element={<Protected><LeadsPage /></Protected>} />
    <Route path="case-studies" element={<Protected><CaseStudiesPage /></Protected>} />
  </Routes>
);

export default Admin;
